import {
	DateField,
	DateTimeField,
	MultiSelectField,
	SingleSelectField,
	TextField,
	TextareaField,
	TimeField,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { DataFieldType } from "../../../shared/types/dataField";
import { useDataFieldsQuery } from "../shared/api/dataFieldService";
import { ContactAttributeResourceDataField } from "../shared/types";
import { BetweenDateTimeValueField, BetweenDateValueField } from "./BetweenValueField";
import { CheckboxValueField } from "./CheckboxValueField";
import { ContactFilterOperatorAndValue } from "./ContactFilterOperatorAndValue";
import { FilterCreatableMultiSelect } from "./FilterCreatableMultiSelect";
import { IFilterEntryDescription } from "./types";

interface Props {
	name: string;
	attribute: ContactAttributeResourceDataField;
}

export const ContactFilterDataFieldEntry: FC<Props> = ({ name, attribute }) => {
	const { data: dataFields } = useDataFieldsQuery();
	const dataField = useMemo(
		() => dataFields.find(({ id }) => id === attribute.entityType),
		[dataFields, attribute.entityType],
	);

	const descriptions: Record<DataFieldType, IFilterEntryDescription> = useMemo(
		() => ({
			[DataFieldType.DATE]: {
				operators: [
					"IS_EMPTY",
					"IS_NOT_EMPTY",
					"GREATER_THAN_EQUAL",
					"LESS_THAN_EQUAL",
					"EQUAL",
					"NOT_EQUAL",
					"BETWEEN",
				],
				render: (props, operator) => {
					switch (operator) {
						case "BETWEEN":
							return <BetweenDateValueField name={props.name} />;
						default:
							return <DateField {...props} />;
					}
				},
			},
			[DataFieldType.DATETIME]: {
				operators: [
					"IS_EMPTY",
					"IS_NOT_EMPTY",
					"GREATER_THAN_EQUAL",
					"LESS_THAN_EQUAL",
					"EQUAL",
					"NOT_EQUAL",
					"BETWEEN",
				],
				render: (props, operator) => {
					switch (operator) {
						case "BETWEEN":
							return <BetweenDateTimeValueField name={props.name} />;
						default:
							return <DateTimeField {...props} />;
					}
				},
			},

			[DataFieldType.CHECKBOX]: {
				operators: ["EQUAL"],
				render: (props) => <CheckboxValueField name={props.name} />,
			},
			[DataFieldType.CHECKBOX_GROUP]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
				render: (props) => {
					if (!dataField) return null;
					return (
						<MultiSelectField
							{...props}
							options={dataField.options}
							getLabel={(option) => option.value}
							clearable={true}
						/>
					);
				},
			},
			[DataFieldType.EMAIL]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL", "WHERE_IN_CONTAINS"],
				render: (props, operator) => {
					switch (operator) {
						case "WHERE_IN_CONTAINS":
							return <FilterCreatableMultiSelect {...props} />;
						default:
							return <TextField {...props} />;
					}
				},
			},
			[DataFieldType.LONG_TEXT]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
				render: (props) => <TextareaField {...props} />,
			},
			[DataFieldType.NUMBER]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "GREATER_THAN_EQUAL", "LESS_THAN_EQUAL", "EQUAL", "NOT_EQUAL"],
				render: (props) => <TextField type="number" {...props} />,
			},
			[DataFieldType.URL]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
				render: (props) => <TextField {...props} />,
			},
			[DataFieldType.PHONE]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
				render: (props) => <TextField type="tel" {...props} />,
			},
			[DataFieldType.MULTI_SELECT_FIELD]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
				render: (props) => {
					if (!dataField) return null;
					return (
						<MultiSelectField
							{...props}
							options={dataField.options}
							getLabel={(option) => option.value}
							clearable={true}
						/>
					);
				},
			},
			[DataFieldType.RADIO]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
				render: (props, operator) => {
					if (!dataField) return null;
					switch (operator) {
						case "WHERE_IN":
							return (
								<MultiSelectField
									{...props}
									options={dataField.options}
									getLabel={(option) => option.value}
									clearable={true}
								/>
							);
						default:
							return (
								<SingleSelectField
									{...props}
									options={dataField.options}
									getLabel={(option) => option.value}
									clearable={true}
								/>
							);
					}
				},
			},
			[DataFieldType.TIME]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "GREATER_THAN_EQUAL", "LESS_THAN_EQUAL", "EQUAL", "NOT_EQUAL"],
				render: (props) => <TimeField {...props} />,
			},
			[DataFieldType.TEXT]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
				render: (props) => <TextField {...props} />,
			},
			[DataFieldType.SELECT_FIELD]: {
				operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
				render: (props, operator) => {
					if (!dataField) return null;
					switch (operator) {
						case "WHERE_IN":
							return (
								<MultiSelectField
									{...props}
									options={dataField.options}
									getLabel={(option) => option.value}
									clearable={true}
								/>
							);
						default:
							return (
								<SingleSelectField
									{...props}
									options={dataField.options}
									getLabel={(option) => option.value}
									clearable={true}
								/>
							);
					}
				},
			},
		}),
		[dataField],
	);

	if (!dataField) return null;

	return (
		<ContactFilterOperatorAndValue name={name} type={attribute.entityType} description={descriptions[dataField.type]} />
	);
};
